import { useState, useCallback } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import Divider from '@mui/material/Divider'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import api from 'services/api'
import { Header, Text } from './Bits'

const SIDE_A_COLOR = '#2196f3'
const SIDE_B_COLOR = '#ff9800'

const TYPE_AI = 'ai_auto_narrative'
const TYPE_EMOTICON = 'emoticon'

const STYLE_EMOJI = 'emoji'
const STYLE_REMOJI = 'remoji'

const PROMPT_STANDARD = 'default'
const PROMPT_AGGRESSIVE = 'aggressive_scenes'
const PROMPT_SPEAKER_ICONS = 'speaker_icons'
const PROMPT_LABELS = {
  [PROMPT_STANDARD]: 'Standard prompt (production)',
  [PROMPT_AGGRESSIVE]: 'Aggressive scenes (experimental)',
  [PROMPT_SPEAKER_ICONS]: 'Speaker icons (code-driven)',
}

const SEGMENT_ANIMATE = 'animate'
const SEGMENT_PRODUCTION = 'production'
const SEGMENT_MANUAL = 'manual'

const SOURCE_ID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

// Accepts a raw source ID, a share code, or a pasted share link and
// returns the reclip's source_id (resolving via the shares API if needed).
const resolveSourceId = async (raw) => {
  const value = raw.trim()
  if (SOURCE_ID_RE.test(value)) return value
  const shareCode = value.split('/').slice(-1)[0].split(/[?#]/)[0]
  if (!shareCode) throw new Error('Enter a source ID or share link')
  const share = await api.shares.getShare(shareCode)
  return share.reclip_source_id
}

const AI_STYLES = [
  'auto',
  'blobs',
  'claymation',
  'papercraft',
  'action_figures',
  'memecore',
  'deep_fried',
  'lofi',
  'lunchbox',
  'mfr',
  'pets',
  'physical_comedy',
  'shonen_anime',
  'skibidi',
  'southpark',
]

const inputSx = {
  '& input, & textarea': {
    backgroundColor: 'background.paper',
    width: '100%',
    display: 'block',
    border: 0,
    p: 1.5,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'divider',
    borderRadius: '8px',
    fontSize: '0.95em',
    resize: 'vertical',
    '&:focus': { borderColor: 'white', outline: 'none' },
  },
}

const selectSx = {
  fontSize: '0.85em',
  backgroundColor: 'background.paper',
  '& .MuiSelect-select': { py: 0.75 },
}

// Simple mode is built for the remoji prompt trial: both sides locked to
// emoticon/remoji on the current trim with context included, differing only
// by prompt variant.
const defaultSideConfig = (promptVariant = PROMPT_STANDARD) => ({
  segmentSource: SEGMENT_PRODUCTION,
  manualStart: '',
  manualEnd: '',
  animationType: TYPE_EMOTICON,
  emoticonStyle: STYLE_REMOJI,
  promptVariant,
  useContext: true,
  style: 'auto',
})

// Clearing discards the animate segment, so any side still pointed at it
// falls back to the production trim — reflect that in the select.
const resetAnimateSegmentSource = (config) =>
  config.segmentSource === SEGMENT_ANIMATE
    ? { ...config, segmentSource: SEGMENT_PRODUCTION }
    : config

const SideConfig = ({
  label,
  accentColor,
  config,
  onChange,
  hasPrepared,
  advanced,
}) => {
  const set = (field) => (value) => onChange({ ...config, [field]: value })

  const showPromptSelect =
    config.animationType === TYPE_EMOTICON &&
    config.emoticonStyle === STYLE_REMOJI

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: accentColor,
            mr: 1,
          }}
        />
        <Header text={label} mt={0} />
      </Box>

      {advanced && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
          <Select
            value={config.animationType}
            onChange={(e) => set('animationType')(e.target.value)}
            size="small"
            sx={{ ...selectSx, flex: 1 }}
          >
            <MenuItem value={TYPE_AI}>AI Narrative</MenuItem>
            <MenuItem value={TYPE_EMOTICON}>Emoticon</MenuItem>
          </Select>
          {config.animationType === TYPE_AI && (
            <Select
              value={config.style}
              onChange={(e) => set('style')(e.target.value)}
              size="small"
              sx={{ ...selectSx, flex: 1 }}
            >
              {AI_STYLES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          )}
          {config.animationType === TYPE_EMOTICON && (
            <Select
              value={config.emoticonStyle}
              onChange={(e) => set('emoticonStyle')(e.target.value)}
              size="small"
              sx={{ ...selectSx, flex: 1 }}
            >
              <MenuItem value={STYLE_EMOJI}>Emoji</MenuItem>
              <MenuItem value={STYLE_REMOJI}>Remoji</MenuItem>
            </Select>
          )}
        </Box>
      )}

      {showPromptSelect && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
          <Select
            value={config.promptVariant}
            onChange={(e) => set('promptVariant')(e.target.value)}
            size="small"
            sx={{ ...selectSx, flex: 1 }}
          >
            <MenuItem value={PROMPT_STANDARD}>
              {PROMPT_LABELS[PROMPT_STANDARD]}
            </MenuItem>
            <MenuItem value={PROMPT_AGGRESSIVE}>
              {PROMPT_LABELS[PROMPT_AGGRESSIVE]}
            </MenuItem>
            <MenuItem value={PROMPT_SPEAKER_ICONS}>
              {PROMPT_LABELS[PROMPT_SPEAKER_ICONS]}
            </MenuItem>
          </Select>
        </Box>
      )}

      {advanced && (
        <>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
            <Select
              value={config.segmentSource}
              onChange={(e) => set('segmentSource')(e.target.value)}
              size="small"
              sx={{ ...selectSx, flex: 1 }}
            >
              <MenuItem value={SEGMENT_PRODUCTION}>Current trim</MenuItem>
              <MenuItem value={SEGMENT_ANIMATE} disabled={!hasPrepared}>
                Animate-preset segment
              </MenuItem>
              <MenuItem value={SEGMENT_MANUAL}>Manual start/end</MenuItem>
            </Select>
            {config.segmentSource === SEGMENT_MANUAL && (
              <Box sx={{ display: 'flex', gap: 1, ...inputSx }}>
                <input
                  style={{ width: 70 }}
                  value={config.manualStart}
                  onChange={(e) => set('manualStart')(e.target.value)}
                  placeholder="start s"
                />
                <input
                  style={{ width: 70 }}
                  value={config.manualEnd}
                  onChange={(e) => set('manualEnd')(e.target.value)}
                  placeholder="end s"
                />
              </Box>
            )}
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={config.useContext}
                onChange={(e) => set('useContext')(e.target.checked)}
              />
            }
            label={
              <Box sx={{ fontSize: '0.82em' }}>Include animation context</Box>
            }
            sx={{ mt: 0.5 }}
          />
        </>
      )}
    </Box>
  )
}

const Vignette = ({ vignette, index }) => (
  <Box sx={{ mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
    <Box sx={{ fontSize: '0.75em', color: 'text.secondary', mb: 0.5 }}>
      #{index + 1}
      {vignette.start_time_in_seconds != null &&
        vignette.end_time_in_seconds != null &&
        ` · ${vignette.start_time_in_seconds}s – ${vignette.end_time_in_seconds}s`}
      {vignette.characters_in_scene?.length > 0 &&
        ` · ${vignette.characters_in_scene.join(', ')}`}
      {vignette.locations_in_scene?.length > 0 &&
        ` · ${vignette.locations_in_scene.join(', ')}`}
    </Box>
    <Box sx={{ fontSize: '0.82em', whiteSpace: 'pre-wrap' }}>
      {vignette.directors_notes}
    </Box>
  </Box>
)

// Remoji picks reference a branded asset (asset_id + optional variant)
// instead of a Unicode emoji; the label is Gemini's description of the
// moment. Show both so it's clear which asset was chosen and why.
const AssetName = ({ assetId, variant }) => (
  <Box
    component="span"
    sx={{
      fontFamily: 'monospace',
      backgroundColor: 'action.selected',
      borderRadius: 1,
      px: 0.5,
      py: 0.1,
    }}
  >
    {assetId}
    {variant ? `:${variant}` : ''}
  </Box>
)

const EmoticonScene = ({ scene, index }) => (
  <Box sx={{ mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
    <Box sx={{ fontSize: '0.75em', color: 'text.secondary', mb: 0.5 }}>
      #{index + 1}
      {scene.start_timestamp != null &&
        scene.end_timestamp != null &&
        ` · ${scene.start_timestamp}s – ${scene.end_timestamp}s`}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      {scene.primary_emoji && (
        <Box sx={{ fontSize: '1.5em' }}>{scene.primary_emoji}</Box>
      )}
      {scene.primary_asset_id && (
        <Box sx={{ fontSize: '0.8em' }}>
          <AssetName
            assetId={scene.primary_asset_id}
            variant={scene.primary_asset_variant}
          />
        </Box>
      )}
      <Box sx={{ fontSize: '0.85em', fontWeight: 700 }}>
        {scene.primary_label}
      </Box>
    </Box>
    {scene.secondary_emojis?.length > 0 && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {scene.secondary_emojis.map((se, i) => (
          <Box
            key={i}
            sx={{
              fontSize: '0.75em',
              backgroundColor: 'action.hover',
              borderRadius: 1,
              px: 0.75,
              py: 0.25,
            }}
          >
            {se.asset_id ? (
              <AssetName assetId={se.asset_id} variant={se.asset_variant} />
            ) : (
              se.emoji
            )}{' '}
            {se.label}{' '}
            {se.timestamp != null && (
              <span style={{ opacity: 0.6 }}>@{se.timestamp}s</span>
            )}
          </Box>
        ))}
      </Box>
    )}
  </Box>
)

const OutlineSection = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Box
      sx={{
        fontSize: '0.72em',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: 'text.secondary',
        mb: 0.5,
      }}
    >
      {title}
    </Box>
    {children}
  </Box>
)

const ResultColumn = ({
  label,
  accentColor,
  result,
  error,
  renderState,
  onRender,
}) => {
  if (error) {
    return (
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontWeight: 700, fontSize: '0.9em', mb: 1 }}>{label}</Box>
        <Box sx={{ color: 'error.main', fontSize: '0.85em' }}>{error}</Box>
      </Box>
    )
  }
  if (!result) return null

  const outline = result.outline || {}

  return (
    <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: accentColor,
            mr: 1,
            flexShrink: 0,
          }}
        />
        <Box sx={{ fontWeight: 700, fontSize: '0.9em' }}>{label}</Box>
      </Box>

      <Box sx={{ fontSize: '0.78em', color: 'text.secondary', mb: 1.5 }}>
        {(result.latency_ms / 1000).toFixed(1)}s &middot;{' '}
        {result.animation_type === TYPE_AI ? 'AI Narrative' : 'Emoticon'}{' '}
        &middot; {result.start_s}s &ndash; {result.end_s}s
        {result.animation_type === TYPE_EMOTICON && result.style_used && (
          <Box>
            Style: {result.style_used}
            {result.prompt_variant_used &&
              ` · Prompt: ${
                PROMPT_LABELS[result.prompt_variant_used] ||
                result.prompt_variant_used
              }`}
            {result.model_used && ` · ${result.model_used}`}
          </Box>
        )}
        {result.title_used && <Box>Title: {result.title_used}</Box>}
        <Box>
          Context:{' '}
          {result.animation_context_used
            ? `"${result.animation_context_used}"`
            : 'none'}
        </Box>
      </Box>

      {outline.creative_strategy && (
        <OutlineSection title="Creative Strategy">
          <Box sx={{ fontSize: '0.82em', whiteSpace: 'pre-wrap' }}>
            {outline.creative_strategy}
          </Box>
        </OutlineSection>
      )}

      {outline.style && (
        <OutlineSection title="Style">
          <Box sx={{ fontSize: '0.82em', whiteSpace: 'pre-wrap' }}>
            {outline.style}
          </Box>
        </OutlineSection>
      )}

      {outline.characters?.length > 0 && (
        <OutlineSection title={`Characters (${outline.characters.length})`}>
          {outline.characters.map((c, i) => (
            <Box key={i} sx={{ mb: 1 }}>
              <Box sx={{ fontSize: '0.82em', fontWeight: 700 }}>{c.name}</Box>
              <Box sx={{ fontSize: '0.78em', color: 'text.secondary' }}>
                {c.description}
              </Box>
            </Box>
          ))}
        </OutlineSection>
      )}

      {outline.locations?.length > 0 && (
        <OutlineSection title={`Locations (${outline.locations.length})`}>
          {outline.locations.map((l, i) => (
            <Box key={i} sx={{ mb: 1 }}>
              <Box sx={{ fontSize: '0.82em', fontWeight: 700 }}>{l.name}</Box>
              <Box sx={{ fontSize: '0.78em', color: 'text.secondary' }}>
                {l.description}
              </Box>
            </Box>
          ))}
        </OutlineSection>
      )}

      {outline.vignettes?.length > 0 && (
        <OutlineSection title={`Vignettes (${outline.vignettes.length})`}>
          {outline.vignettes.map((v, i) => (
            <Vignette key={i} vignette={v} index={i} />
          ))}
        </OutlineSection>
      )}

      {outline.scenes?.length > 0 && (
        <OutlineSection title={`Scenes (${outline.scenes.length})`}>
          {outline.scenes.map((s, i) => (
            <EmoticonScene key={i} scene={s} index={i} />
          ))}
        </OutlineSection>
      )}

      <Box sx={{ mt: 1, mb: 2 }}>
        <LoadingButton
          size="small"
          variant="outlined"
          onClick={onRender}
          loading={renderState?.isRendering}
          disabled={!!renderState?.video}
        >
          {result.animation_type === TYPE_AI
            ? 'Render Video (Veo, costs $)'
            : 'Render Video'}
        </LoadingButton>
        {renderState?.error && (
          <Box sx={{ color: 'error.main', fontSize: '0.8em', mt: 1 }}>
            {renderState.error}
          </Box>
        )}
        {renderState?.video && (
          <Box sx={{ mt: 1.5 }}>
            <video
              src={renderState.video.video_url}
              poster={renderState.video.thumbnail_url || undefined}
              controls
              style={{ width: '100%', maxWidth: 360, borderRadius: 8 }}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

const AnimationCompare = () => {
  const [advanced, setAdvanced] = useState(false)
  const [sourceId, setSourceId] = useState('')
  const [isPreparing, setIsPreparing] = useState(false)
  const [prepareResult, setPrepareResult] = useState(null)
  const [animationContext, setAnimationContext] = useState('')
  const [configA, setConfigA] = useState(defaultSideConfig(PROMPT_STANDARD))
  const [configB, setConfigB] = useState(defaultSideConfig(PROMPT_AGGRESSIVE))
  const [isComparing, setIsComparing] = useState(false)
  const [resultA, setResultA] = useState(null)
  const [resultB, setResultB] = useState(null)
  const [errorA, setErrorA] = useState(null)
  const [errorB, setErrorB] = useState(null)
  const [error, setError] = useState(null)
  const [renderStateA, setRenderStateA] = useState(null)
  const [renderStateB, setRenderStateB] = useState(null)

  const animateSegment = prepareResult?.moments?.[0] || null

  // Leaving advanced mode resets hidden controls to the simple-mode
  // defaults so nothing stays configured in an invisible state.
  const handleAdvancedToggle = useCallback((checked) => {
    setAdvanced(checked)
    if (!checked) {
      setConfigA((c) => ({
        ...defaultSideConfig(PROMPT_STANDARD),
        promptVariant: c.promptVariant,
      }))
      setConfigB((c) => ({
        ...defaultSideConfig(PROMPT_AGGRESSIVE),
        promptVariant: c.promptVariant,
      }))
    }
  }, [])

  const handlePrepare = useCallback(async () => {
    if (!sourceId.trim()) return
    setIsPreparing(true)
    setError(null)
    try {
      const resolvedId = await resolveSourceId(sourceId)
      setSourceId(resolvedId)
      const data = await api.autoTrim.testAutoTrim(resolvedId, {
        preset: 'animate',
      })
      setPrepareResult(data)
      setAnimationContext(data.animation_context || '')
    } catch (err) {
      setError(err.message || 'Prepare failed')
    } finally {
      setIsPreparing(false)
    }
  }, [sourceId])

  const buildOutlineParams = useCallback(
    (config) => {
      const params = {
        animationType: config.animationType,
      }
      if (config.segmentSource === SEGMENT_ANIMATE && animateSegment) {
        params.startS = animateSegment.start_s
        params.endS = animateSegment.end_s
      } else if (config.segmentSource === SEGMENT_MANUAL) {
        const start = parseFloat(config.manualStart)
        const end = parseFloat(config.manualEnd)
        if (!isNaN(start)) params.startS = start
        if (!isNaN(end)) params.endS = end
      }
      if (config.useContext && animationContext.trim()) {
        params.animationContext = animationContext.trim()
      }
      if (config.animationType === TYPE_AI && config.style !== 'auto') {
        params.style = config.style
      }
      if (config.animationType === TYPE_EMOTICON) {
        params.style = config.emoticonStyle
        if (config.emoticonStyle === STYLE_REMOJI) {
          params.promptVariant = config.promptVariant
        }
      }
      return params
    },
    [animateSegment, animationContext]
  )

  const handleCompare = useCallback(async () => {
    if (!sourceId.trim()) return
    setIsComparing(true)
    setResultA(null)
    setResultB(null)
    setErrorA(null)
    setErrorB(null)
    setError(null)
    setRenderStateA(null)
    setRenderStateB(null)

    let resolvedId
    try {
      resolvedId = await resolveSourceId(sourceId)
      setSourceId(resolvedId)
    } catch (err) {
      setError(err.message || 'Could not resolve reclip')
      setIsComparing(false)
      return
    }

    const callA = api.animationTest.testOutline(
      resolvedId,
      buildOutlineParams(configA)
    )
    const callB = api.animationTest.testOutline(
      resolvedId,
      buildOutlineParams(configB)
    )

    Promise.allSettled([callA, callB])
      .then(([a, b]) => {
        if (a.status === 'fulfilled') setResultA(a.value)
        else setErrorA(a.reason?.message || 'Outline A failed')
        if (b.status === 'fulfilled') setResultB(b.value)
        else setErrorB(b.reason?.message || 'Outline B failed')
      })
      .finally(() => setIsComparing(false))
  }, [sourceId, configA, configB, buildOutlineParams])

  const makeRenderHandler = useCallback(
    (result, setRenderState) => () => {
      if (!result) return
      setRenderState({ isRendering: true })
      api.animationTest
        .testRender(sourceId.trim(), {
          animationType: result.animation_type,
          outline: result.outline,
          startS: result.start_s,
          endS: result.end_s,
          style: result.style_used || undefined,
        })
        .then((video) => setRenderState({ video }))
        .catch((err) =>
          setRenderState({ error: err.message || 'Render failed' })
        )
    },
    [sourceId]
  )

  const handleClear = useCallback(() => {
    setSourceId('')
    setPrepareResult(null)
    setAnimationContext('')
    setConfigA(resetAnimateSegmentSource)
    setConfigB(resetAnimateSegmentSource)
    setResultA(null)
    setResultB(null)
    setErrorA(null)
    setErrorB(null)
    setError(null)
    setRenderStateA(null)
    setRenderStateB(null)
  }, [])

  const hasResults = resultA || resultB || errorA || errorB

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Left panel: config */}
      <Box sx={{ width: 500, height: '100%', overflow: 'auto', flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Box sx={{ fontSize: '1.2em', fontWeight: 700 }}>
            Animation Playground
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={advanced}
                onChange={(e) => handleAdvancedToggle(e.target.checked)}
              />
            }
            label={<Box sx={{ fontSize: '0.82em' }}>Advanced</Box>}
            sx={{ mr: 0 }}
          />
        </Box>
        {advanced ? (
          <Text>
            Compare animation outlines side-by-side on a single clip — with or
            without segment-focused context from the full recording. Nothing is
            persisted. Use "Get Context" to run the animate trim preset and
            fetch its recommended segment and context first.
          </Text>
        ) : (
          <Text>
            Try the two Remoji prompts side-by-side on a single clip. Paste a
            share link or source ID, then click "Compare Outlines". Nothing is
            saved to the reclip.
          </Text>
        )}

        <Header text="Share Link or Source ID" />
        <Box sx={inputSx}>
          <input
            value={sourceId}
            onChange={(e) => setSourceId(e.target.value)}
            placeholder="e.g. https://www.reclip.app/s/12345678 or a source ID"
          />
        </Box>

        {advanced && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, gap: 2 }}>
            <LoadingButton
              size="small"
              variant="outlined"
              onClick={handlePrepare}
              disabled={!sourceId.trim()}
              loading={isPreparing}
            >
              Get Context
            </LoadingButton>
            {animateSegment && (
              <Box sx={{ fontSize: '0.78em', color: 'text.secondary' }}>
                Animate segment: {animateSegment.start_s.toFixed(1)}s &ndash;{' '}
                {animateSegment.end_s.toFixed(1)}s
              </Box>
            )}
          </Box>
        )}

        {advanced && animateSegment?.transcript_slice && (
          <Box
            sx={{
              mt: 1,
              p: 1,
              backgroundColor: 'background.paper',
              borderRadius: '8px',
              borderLeft: '3px solid',
              borderColor: 'divider',
              fontSize: '0.78em',
              whiteSpace: 'pre-wrap',
            }}
          >
            {animateSegment.transcript_slice}
          </Box>
        )}

        {prepareResult && advanced && (
          <>
            <Header text="Animation Context (editable)" mt={2} />
            <Box sx={inputSx}>
              <textarea
                value={animationContext}
                onChange={(e) => setAnimationContext(e.target.value)}
                rows={3}
              />
            </Box>
          </>
        )}

        {prepareResult && !advanced && animationContext.trim() && (
          <>
            <Header text="Animation Context (applied to both sides)" mt={2} />
            <Box
              sx={{
                p: 1,
                backgroundColor: 'background.paper',
                borderRadius: '8px',
                fontSize: '0.78em',
                whiteSpace: 'pre-wrap',
              }}
            >
              {animationContext}
            </Box>
          </>
        )}

        <SideConfig
          label="Side A"
          accentColor={SIDE_A_COLOR}
          config={configA}
          onChange={setConfigA}
          hasPrepared={!!animateSegment}
          advanced={advanced}
        />
        <SideConfig
          label="Side B"
          accentColor={SIDE_B_COLOR}
          config={configB}
          onChange={setConfigB}
          hasPrepared={!!animateSegment}
          advanced={advanced}
        />

        <Box
          sx={{
            mt: 2,
            mb: 4,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClear}
            disabled={!sourceId && !hasResults}
          >
            Clear
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleCompare}
            disabled={!sourceId.trim()}
            loading={isComparing}
          >
            Compare Outlines
          </LoadingButton>
        </Box>

        {error && (
          <Box
            sx={{
              backgroundColor: 'background.paper',
              p: 2,
              borderRadius: '8px',
              color: 'error.main',
              whiteSpace: 'pre-wrap',
              fontSize: '0.9em',
            }}
          >
            {error}
          </Box>
        )}
      </Box>

      <Divider orientation="vertical" sx={{ mx: 3 }} />

      {/* Right panel: results */}
      <Box sx={{ flex: 1, height: '100%', overflow: 'auto', minWidth: 0 }}>
        {!hasResults && !isComparing && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.9em', mt: 4 }}>
            Outlines will appear here after running a comparison.
          </Box>
        )}

        {isComparing && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.9em', mt: 4 }}>
            Generating outlines...
          </Box>
        )}

        {hasResults && (
          <Box sx={{ display: 'flex', gap: 3 }}>
            <ResultColumn
              label="Side A"
              accentColor={SIDE_A_COLOR}
              result={resultA}
              error={errorA}
              renderState={renderStateA}
              onRender={makeRenderHandler(resultA, setRenderStateA)}
            />
            <Divider orientation="vertical" flexItem />
            <ResultColumn
              label="Side B"
              accentColor={SIDE_B_COLOR}
              result={resultB}
              error={errorB}
              renderState={renderStateB}
              onRender={makeRenderHandler(resultB, setRenderStateB)}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default AnimationCompare
